import React, { useState, useEffect, useCallback } from "react";
import { Loader, FileSpreadsheet, Users } from "lucide-react";
import api from "../../../../utils/api";
import { fmtD } from "./ui";
import { PalletBoard, PalletTable } from "./PalletLists";
import PalletWindow from "./PalletWindow";
import ContactsPanel from "./ContactsPanel";

const LAYOUT_KEY = "ct.pallet.layout";
const readLayout = () => { try { return localStorage.getItem(LAYOUT_KEY) || "board"; } catch { return "board"; } };

// Pallets view of the Control Tower: where each pallet program is, who holds it and what it waits for
export default function PalletsView({ refreshKey, onUpload }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [layout, setLayout] = useState(readLayout);
    const [openId, setOpenId] = useState(() => {
        const id = new URLSearchParams(window.location.search).get("pallet");
        return id && id.startsWith("PAL-") ? id : null;
    });
    const [showContacts, setShowContacts] = useState(false);
    const [toastMsg, setToastMsg] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await api.get("/api/excel-ingestion/pallets");
            setData(res.data);
            setError(null);
        } catch (e) {
            setError(e.response?.data?.error || "Could not load the pallet programs");
        }
    }, []);

    useEffect(() => { load(); }, [load, refreshKey]);
    useEffect(() => { try { localStorage.setItem(LAYOUT_KEY, layout); } catch { /* storage unavailable */ } }, [layout]);
    useEffect(() => {
        if (!toastMsg) return undefined;
        const t = setTimeout(() => setToastMsg(null), 4200);
        return () => clearTimeout(t);
    }, [toastMsg]);

    const closeWindow = useCallback(() => setOpenId(null), []);

    if (error) return <div className="bg-red-900/30 border border-red-800 rounded-xl px-4 py-3 text-sm text-red-400">{error}</div>;
    if (!data) return <div className="flex justify-center py-16"><Loader size={20} className="animate-spin text-slate-600"/></div>;

    if (!data.uploaded) {
        return (
            <div className="bg-[#111827] border border-[#1F2937] rounded-xl px-6 py-10 text-center space-y-3">
                <p className="text-sm text-slate-300">No pallet tracking workbook has been uploaded yet.</p>
                <p className="text-[12px] text-slate-500">Upload the inbound Pilot Report and outbound carrier report first, so supply and transport can be linked, then the pallet workbook.</p>
                <button onClick={onUpload} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-green-500/40 text-sm text-green-400 hover:bg-green-500/10">
                    <FileSpreadsheet size={14}/> Upload the pallet workbook
                </button>
            </div>
        );
    }

    const { pallets, summary, stages, reportDate } = data;
    const ry = reportDate ? Number(reportDate.slice(0, 4)) : undefined;
    const red = pallets.filter(p => p.popup?.health === "red").length;
    const late = pallets.filter(p => p.custody?.daysLate > 0).length;
    const byTeam = pallets.reduce((a, p) => ((a[p.custody?.sitsWith] = (a[p.custody?.sitsWith] || 0) + 1), a), {});
    const KPIS = [
        { n: pallets.length, label: "Pallet programs", sub: `${(summary.totalPalletQty ?? 0).toLocaleString()} pallets · ${(summary.totalUnits ?? 0).toLocaleString()} units` },
        { n: late, label: "Behind at current stage", sub: `${red} need action`, bad: late > 0 },
        { n: summary.milestonesOverdue ?? 0, label: "Overdue milestones", sub: `${summary.crossChecksFailed ?? 0} failed cross-checks` },
        { n: data.queuedEmails, label: "Emails queued", sub: Object.entries(byTeam).map(([t, n]) => `${t} ${n}`).join(" · ") },
    ];

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="text-xl font-bold text-white">Pallet programs</h2>
                    <p className="text-[11px] text-slate-500">
                        Report {fmtD(reportDate, 0)} · {data.fileName}
                        {data.source?.linkedInboundUpload && ` · linked to ${data.source.linkedInboundUpload}`}
                        {data.source?.linkedOutboundUpload && ` and ${data.source.linkedOutboundUpload}`}
                    </p>
                </div>
                <button onClick={() => setShowContacts(true)}
                    className="flex items-center gap-2 px-3 py-2 bg-[#111827] border border-[#1F2937] rounded-lg text-sm text-slate-300 hover:border-slate-500">
                    <Users size={14}/> Notification contacts
                </button>
            </div>

            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                {KPIS.map(k => (
                    <div key={k.label} className="bg-[#111827] border border-[#1F2937] rounded-xl p-4">
                        <p className={`text-2xl font-bold ${k.bad ? "text-red-400" : "text-white"}`}>{k.n}</p>
                        <p className="text-[12px] font-semibold text-slate-200 mt-1">{k.label}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{k.sub}</p>
                    </div>
                ))}
            </div>

            <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-white">Where each pallet is</h3>
                <div className="flex bg-[#0D1520] border border-[#1F2937] rounded-lg p-0.5" role="tablist" aria-label="Layout">
                    {[["board", "By stage"], ["table", "Table"]].map(([k, l]) => (
                        <button key={k} role="tab" aria-selected={layout === k} onClick={() => setLayout(k)}
                            className={`px-2.5 py-1 rounded-md text-[11px] ${layout === k ? "bg-[#1F2937] text-white" : "text-slate-500 hover:text-slate-300"}`}>{l}</button>
                    ))}
                </div>
            </div>

            {layout === "board"
                ? <PalletBoard pallets={pallets} stages={stages} reportYear={ry} onOpen={setOpenId}/>
                : <PalletTable pallets={pallets} reportDate={reportDate} reportYear={ry} commentCounts={data.commentCounts || {}} onOpen={setOpenId}/>}

            <p className="text-[11px] text-slate-600">
                Click a pallet to open its window. Data: the latest accepted pallet workbook, Pilot Report and carrier report.
                Emails are queued in the outbox, not sent.
            </p>

            {openId && <PalletWindow palletId={openId} reportDate={reportDate} onClose={closeWindow} onChanged={load} toast={setToastMsg}/>}
            {showContacts && <ContactsPanel teams={data.teams} contacts={data.contacts} onClose={() => setShowContacts(false)}
                onSaved={() => { load(); setToastMsg("Contacts saved"); }}/>}
            {toastMsg && (
                <div role="status" className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#1F2937] border border-[#374151] text-slate-100 text-[12px] px-4 py-2 rounded-lg shadow-xl" style={{ zIndex: 10000 }}>
                    {toastMsg}
                </div>
            )}
        </div>
    );
}
